import {
  Dropdown,
  Field,
  makeStyles,
  Option,
  Switch,
  tokens,
} from '@fluentui/react-components';
import { useT } from '@powerportalspro/react';
import {
  BoolEdit,
  BoolEditorType,
  EditorLabelPosition,
  Section,
  SectionColumn,
} from '@powerportalspro/react-fluent';
import { useMemo, useState } from 'react';

// Localization namespace — same Blazor demo's
// `BoolEditPropertiesDemo` keys so both stacks read from the same JSON.
const STRINGS_BASE =
  'components.PowerPortalsPro.Demo.Client.Customizations.Pages.Editors.BoolEdit.BoolEditPropertiesDemo';

const EDITOR_TYPE_VALUES = [
  BoolEditorType.Checkbox,
  BoolEditorType.Switch,
] as const;
const LABEL_POSITION_VALUES = [
  EditorLabelPosition.Auto,
  EditorLabelPosition.Above,
  EditorLabelPosition.Side,
] as const;

const useStyles = makeStyles({
  toolbar: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: tokens.spacingHorizontalL,
  },
  inlineField: {
    flexShrink: 0,
    gridTemplateColumns: 'auto auto',
    '& label': {
      whiteSpace: 'nowrap',
    },
  },
  selectInput: {
    minWidth: '125px',
  },
});

export function BoolEditPropertiesSample() {
  const styles = useStyles();
  const t = useT();
  const [readOnly, setReadOnly] = useState(false);
  const [required, setRequired] = useState(false);
  const [disabled, setDisabled] = useState(false);
  const [editorType, setEditorType] = useState<BoolEditorType>(BoolEditorType.Checkbox);
  const [labelPosition, setLabelPosition] = useState<EditorLabelPosition>(
    EditorLabelPosition.Auto,
  );
  const [inlineLabel, setInlineLabel] = useState(true);

  // Localization keys use PascalCase enum names (`Checkbox`, `Switch`,
  // `Auto`, `Above`, `Side`) — capitalize the lowercase value to match.
  const editorTypeOptions = useMemo(
    () =>
      EDITOR_TYPE_VALUES.map((value) => ({
        value,
        label: t(
          `${STRINGS_BASE}.bool-editor-types.${value.charAt(0).toUpperCase()}${value.slice(1)}`,
        ),
      })),
    [t],
  );
  const labelPositionOptions = useMemo(
    () =>
      LABEL_POSITION_VALUES.map((value) => ({
        value,
        label: t(
          `${STRINGS_BASE}.label-positions.${value.charAt(0).toUpperCase()}${value.slice(1)}`,
        ),
      })),
    [t],
  );

  return (
    <Section>
      <SectionColumn
        header={
          <div className={styles.toolbar}>
            <Switch
              label={t(`${STRINGS_BASE}.readonly`)}
              labelPosition="before"
              checked={readOnly}
              onChange={(_, d) => setReadOnly(d.checked)}
            />
            <Switch
              label={t(`${STRINGS_BASE}.required`)}
              labelPosition="before"
              checked={required}
              onChange={(_, d) => setRequired(d.checked)}
            />
            <Switch
              label={t(`${STRINGS_BASE}.disabled`)}
              labelPosition="before"
              checked={disabled}
              onChange={(_, d) => setDisabled(d.checked)}
            />
            <Switch
              label={t(`${STRINGS_BASE}.inline-label`)}
              labelPosition="before"
              checked={inlineLabel}
              onChange={(_, d) => setInlineLabel(d.checked)}
            />
            <Field
              label={t(`${STRINGS_BASE}.bool-editor-type`)}
              orientation="horizontal"
              className={styles.inlineField}
            >
              <Dropdown
                className={styles.selectInput}
                value={
                  editorTypeOptions.find((o) => o.value === editorType)?.label ?? editorType
                }
                selectedOptions={[editorType]}
                onOptionSelect={(_, d) => {
                  if (d.optionValue) setEditorType(d.optionValue as BoolEditorType);
                }}
              >
                {editorTypeOptions.map((opt) => (
                  <Option key={opt.value} value={opt.value}>
                    {opt.label}
                  </Option>
                ))}
              </Dropdown>
            </Field>
            <Field
              label={t(`${STRINGS_BASE}.label-position`)}
              orientation="horizontal"
              className={styles.inlineField}
            >
              <Dropdown
                className={styles.selectInput}
                value={
                  labelPositionOptions.find((o) => o.value === labelPosition)?.label ?? labelPosition
                }
                selectedOptions={[labelPosition]}
                onOptionSelect={(_, d) => {
                  if (d.optionValue) setLabelPosition(d.optionValue as EditorLabelPosition);
                }}
              >
                {labelPositionOptions.map((opt) => (
                  <Option key={opt.value} value={opt.value}>
                    {opt.label}
                  </Option>
                ))}
              </Dropdown>
            </Field>
          </div>
        }
      >
        <BoolEdit
          columnName="ppp_credithold"
          required={required}
          readOnly={readOnly}
          disabled={disabled}
          editorType={editorType}
          labelPosition={labelPosition}
          inlineLabel={inlineLabel}
        />
      </SectionColumn>
    </Section>
  );
}
